/**
 * The bar that is the same everywhere.
 *
 * Extracted so the shop shell and the job workspace can be separate components
 * without either duplicating it. That separation is what lets a job workspace
 * be keyed on its reference — opening a second job must not inherit the first
 * job's verdicts, and the only reliable way to guarantee that is a fresh mount.
 */

import type { ReactNode } from 'react';
import { useSession } from './SessionProvider.js';
import { LANGUAGES } from '../intl/i18n.js';

export function TopBar({ nav }: { nav: ReactNode }) {
  const { session, t, setLanguage, languageError, switchShop, signOut } = useSession();
  if (!session) return null;
  const { shop, country, availableShops } = session;

  return (
    <header className="topbar">
      <span className="brand">Estimator</span>
      <nav className="gnav">{nav}</nav>
      <div className="right">
        {availableShops.length > 1 ? (
          <select value={shop.id} aria-label="Shop"
                  onChange={(e) => switchShop(e.target.value)}>
            {availableShops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        ) : <span className="shopname">{shop.name}</span>}
        <span className="meta">{country.currencyCode} · {country.taxName}</span>
        <select value={session.language} aria-label={t('Language')}
                onChange={(e) => void setLanguage(e.target.value)}>
          {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.nativeName}</option>)}
        </select>
        {languageError && (
          <span className="flag need" role="status" title={languageError}>
            Language not saved
          </span>
        )}
        {/* Static link, on purpose: the guide is a separately-hosted page that
            gets updated in place (same URL, new content) whenever the shown
            workflow changes, so this app never needs a redeploy just because
            the guide's wording did. */}
        <a className="iconbtn amber" href="https://claude.ai/code/artifact/23f23838-526c-42a0-9ad4-36f3bd11569f"
           target="_blank" rel="noopener noreferrer" title="Guide" aria-label="Guide">
          {'\u{1F4D8}'}
        </a>
        <button className="iconbtn red" onClick={() => void signOut()}
                title={t('Sign out')} aria-label={t('Sign out')}>
          {'\u{1F6AA}'}
        </button>
      </div>
    </header>
  );
}

export function ShellFooter({ usingFixtures }: { usingFixtures: boolean }) {
  const { session } = useSession();
  if (!session) return null;
  return (
    <footer className="shell-foot muted small">
      {session.user.email} · {session.role} · {session.shop.name}
      {usingFixtures && (
        <span className="flag need" style={{ marginInlineStart: '0.75rem' }}>
          Worked example · not saved
        </span>
      )}
    </footer>
  );
}
